/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from "react";
import { useAuth as useClerkAuth, useUser } from "@clerk/clerk-react";

export interface AuthValue {
  isLoaded: boolean;
  isSignedIn: boolean;
  fixture: boolean;
  user: { id: string; fullName: string; email: string; imageUrl?: string } | null;
  sessionClaims: { locale?: "de" | "en" | "el"; timezone?: string; cons?: { v?: unknown; exp?: unknown } } | null;
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
  signOut: () => Promise<void>;
}
const Context = createContext<AuthValue | null>(null);

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const auth = useClerkAuth();
  const { user } = useUser();
  const claims = auth.sessionClaims as (AuthValue["sessionClaims"] & Record<string, unknown>) | null | undefined;
  const value: AuthValue = {
    isLoaded: auth.isLoaded,
    isSignedIn: Boolean(auth.isSignedIn),
    fixture: false,
    user: user ? { id: user.id, fullName: user.fullName ?? user.firstName ?? "Kitsos user", email: user.primaryEmailAddress?.emailAddress ?? "", imageUrl: user.imageUrl } : null,
    sessionClaims: claims ?? null,
    getToken: options => auth.getToken(options),
    signOut: async () => { await auth.signOut(); }
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

const fixtureValue: AuthValue = {
  isLoaded: true, isSignedIn: true, fixture: true,
  user: { id: "user_development", fullName: "Kitsos Developer", email: "developer@example.invalid" },
  sessionClaims: { locale: "en", timezone: "Europe/Athens" },
  getToken: async () => "development-fixture-token",
  signOut: async () => undefined
};

export function AuthProvider({ fixture, children }: { fixture: boolean; children: ReactNode }) {
  return fixture ? <Context.Provider value={fixtureValue}>{children}</Context.Provider> : <ClerkAuthBridge>{children}</ClerkAuthBridge>;
}
export function useAuth() { const value = useContext(Context); if (!value) throw new Error("AuthProvider missing"); return value; }
